import React from "react";
import { useParams } from "react-router-dom";

import { TableLoader } from "ui/data-grid";
import type { FormBuilderActions } from "containers/admin/clients/client/components/form-builder/form-builder-action-button";
import FormPageLayout, {
  type FormPageBreadcrumbConfig,
  type FormPageHeaderConfig,
  type FormPageTab,
} from "containers/admin/clients/client/components/form-page-layout/form-page-layout";
import { useClientStrategyForm } from "containers/admin/clients/client/components/client-strategy-page/use-client-strategy-form";

import type { ClientStrategyCategoryDTOStrategyCategory } from "generated/schemas";

export type ClientStrategyPageConfig = {
  strategyCategory: ClientStrategyCategoryDTOStrategyCategory;
  formId: string;
  sidebarSectionId: string;
  breadcrumb: FormPageBreadcrumbConfig;
  header?: FormPageHeaderConfig;
  emptyMessage: string;
  saveSuccessTitle: string;
  saveErrorTitle: string;
  editButtonLabel: string;
  renderPageHeader?: (editActions: React.ReactNode) => React.ReactNode;
  formActions?: FormBuilderActions;
};

const ClientStrategyPage: React.FC<ClientStrategyPageConfig> = ({
  strategyCategory,
  formId,
  sidebarSectionId,
  breadcrumb,
  header,
  emptyMessage,
  saveSuccessTitle,
  saveErrorTitle,
  editButtonLabel,
  renderPageHeader,
  formActions,
}) => {
  const { clientId } = useParams<{ clientId: string }>();
  const {
    tabs,
    sidebarItems,
    isPageReady,
    isEditing,
    setIsEditing,
    hasChanges,
    tabRevisions,
    getTabValues,
    handleTabValuesChange,
    handleDiscard,
    handleSubmit,
    isSaving,
  } = useClientStrategyForm(strategyCategory);

  const handleFormSubmit = (formData: Record<string, unknown>, tab: FormPageTab) => {
    handleSubmit(formData, tab, saveSuccessTitle, saveErrorTitle);
  };

  if (!isPageReady) {
    return <TableLoader isLoading fullScreen />;
  }

  if (!tabs.length) {
    return (
      <div className="flex flex-1 items-center justify-center p-6 text-gray-600">
        {emptyMessage}
      </div>
    );
  }

  if (!clientId) {
    return null;
  }

  return (
    <FormPageLayout
      formId={formId}
      sidebarSectionId={sidebarSectionId}
      tabs={tabs}
      sidebarItems={sidebarItems}
      getTabValues={getTabValues}
      onTabValuesChange={handleTabValuesChange}
      tabRevisions={tabRevisions}
      isSaveDisabled={!hasChanges}
      isDiscardDisabled={!hasChanges}
      isSaving={isSaving}
      isEditing={isEditing}
      onIsEditingChange={setIsEditing}
      editButtonLabel={editButtonLabel}
      onSubmit={handleFormSubmit}
      onDiscard={handleDiscard}
      header={header}
      breadcrumb={breadcrumb}
      renderPageHeader={renderPageHeader}
      formActions={formActions}
    />
  );
};

export default ClientStrategyPage;
